import React, { useState } from "react";
import axios from "axios";
import config from "../config";

export default function ContactUs({ id }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submittedData, setSubmittedData] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${config.apiUrl}/contact`, formData, {
        headers: { "Content-Type": "application/json" },
      });
      setSubmittedData({ ...formData });
      setSubmitted(true);
      setErrors({});
      setFormData({ name: "", email: "", phone: "", city: "", message: "" });
    } catch (error) {
      if (error.response?.status === 422) {
        const apiErrors = error.response.data.errors || {};
        setErrors({
          name: apiErrors.name?.[0] || "",
          email: apiErrors.email?.[0] || "",
          phone: apiErrors.phone?.[0] || "",
          city: apiErrors.city?.[0] || "",
          message: apiErrors.message?.[0] || "",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="contact-section" id={id}>
      {/* Top accent bar */}
      <div className="contact-accent-bar"></div>

      <div className="container">
        <div className="contact-grid">

          {/* Left — info panel */}
          <div className="contact-info-panel">
            <span className="contact-pill">Contact Us</span>
            <h2 className="contact-heading">
              Let's Talk About <span className="contact-heading-accent">Your Goals</span>
            </h2>
            <p className="contact-intro">
              Have a question, partnership inquiry, or just want to say hello?
              We'd love to hear from you. Fill in the form or reach us directly.
            </p>

            <div className="contact-info-cards">
              <div className="contact-info-card">
                <div className="contact-info-icon">
                  <i className="bi bi-envelope-fill"></i>
                </div>
                <div>
                  <div className="contact-info-label">Email us</div>
                  <a href="mailto:techuptechnologies@gmail.com" className="contact-info-value">
                    techuptechnologies@gmail.com
                  </a>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="contact-info-icon">
                  <i className="bi bi-telephone-fill"></i>
                </div>
                <div>
                  <div className="contact-info-label">Call us</div>
                  <a href="tel:+916390008506" className="contact-info-value">
                    6390008506 &nbsp;·&nbsp; 8920976591
                  </a>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="contact-info-icon">
                  <i className="bi bi-geo-alt-fill"></i>
                </div>
                <div>
                  <div className="contact-info-label">Find us</div>
                  <span className="contact-info-value">
                    Kamachha, Varanasi &nbsp;|&nbsp; Sector 75, Noida
                  </span>
                </div>
              </div>
            </div>

            <div className="contact-social-row">
              <a href="https://www.instagram.com/meragym.official" target="_blank" rel="noreferrer" className="contact-social-btn" aria-label="Instagram">
                <i className="bi bi-instagram"></i>
              </a>
              <a href="https://www.facebook.com/meragym" target="_blank" rel="noreferrer" className="contact-social-btn" aria-label="Facebook">
                <i className="bi bi-facebook"></i>
              </a>
              <a href="https://www.youtube.com/@meragym" target="_blank" rel="noreferrer" className="contact-social-btn" aria-label="YouTube">
                <i className="bi bi-youtube"></i>
              </a>
            </div>
          </div>

          {/* Right — form panel */}
          <div className="contact-form-panel">
            {submitted ? (
              <div className="contact-success">
                <div className="contact-success-icon">
                  <i className="bi bi-check-circle-fill"></i>
                </div>
                <h3>Message Sent!</h3>
                <p>Thanks for reaching out. We'll get back to you within 24 hours.</p>
                <a
                  href={`https://wa.me/916390008506?text=${encodeURIComponent(
                    `*New Enquiry — MeraGym*\n\n*Name:* ${submittedData.name}\n*Email:* ${submittedData.email}\n*Phone:* ${submittedData.phone}\n*City:* ${submittedData.city}\n*Message:* ${submittedData.message}`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="contact-whatsapp-btn"
                >
                  <i className="bi bi-whatsapp me-2"></i>Also Send on WhatsApp
                </a>
                <button className="contact-reset-btn mt-3" onClick={() => setSubmitted(false)}>
                  Send Another Message
                </button>
              </div>
            ) : (
              <>
                <div className="contact-form-header">
                  <h3 className="contact-form-title">Send a Message</h3>
                  <p className="contact-form-sub">We typically reply within a few hours.</p>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                  <div className="contact-form-row">
                    <div className="contact-field">
                      <label htmlFor="cf-name">Full Name</label>
                      <input
                        id="cf-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className={errors.name ? "is-invalid" : ""}
                      />
                      {errors.name && <span className="contact-field-error">{errors.name}</span>}
                    </div>

                    <div className="contact-field">
                      <label htmlFor="cf-email">Email Address</label>
                      <input
                        id="cf-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        className={errors.email ? "is-invalid" : ""}
                      />
                      {errors.email && <span className="contact-field-error">{errors.email}</span>}
                    </div>
                  </div>

                  <div className="contact-form-row">
                    <div className="contact-field">
                      <label htmlFor="cf-phone">Phone Number</label>
                      <input
                        id="cf-phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="9XXXXXXXXX"
                        className={errors.phone ? "is-invalid" : ""}
                      />
                      {errors.phone && <span className="contact-field-error">{errors.phone}</span>}
                    </div>

                    <div className="contact-field">
                      <label htmlFor="cf-city">City</label>
                      <input
                        id="cf-city"
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Varanasi"
                        className={errors.city ? "is-invalid" : ""}
                      />
                      {errors.city && <span className="contact-field-error">{errors.city}</span>}
                    </div>
                  </div>

                  <div className="contact-field">
                    <label htmlFor="cf-message">Message</label>
                    <textarea
                      id="cf-message"
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help you..."
                      className={errors.message ? "is-invalid" : ""}
                    ></textarea>
                    {errors.message && <span className="contact-field-error">{errors.message}</span>}
                  </div>

                  <button
                    type="submit"
                    className="contact-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="contact-spinner"></span> Sending…
                      </>
                    ) : (
                      <>
                        Send Message <i className="bi bi-send-fill ms-2"></i>
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
